import React, {
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import * as S from './Accordion.styles';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { sleep } from '@/utils/helpers';
import { ACCORDION_ANIMATION_DURATION } from '@/components/UI/Accordion/Accordion.constants';

type AccordionProps = {
  title: ReactNode;
  subTitle?: ReactNode;
  contentHead?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  maxHeight?: number;
  className?: string;
  onOpen?: () => void;
};

export interface AccordionRef {
  close: () => void;
}

export const Accordion = forwardRef<AccordionRef, AccordionProps>(
  (
    {
      title,
      subTitle,
      children,
      contentHead,
      defaultOpen = false,
      maxHeight,
      className,
      onOpen,
    },
    ref
  ) => {
    const [isOpen, toggle] = useToggleOpenState(defaultOpen);
    const contentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({ close: () => toggle(false) }), []);

    function handleToggle() {
      if (!isOpen && onOpen) {
        sleep(ACCORDION_ANIMATION_DURATION).then(() => onOpen());
      }
      toggle();
    }

    return (
      <S.Wrapper className={className} data-testid="accordion">
        <S.Header onClick={handleToggle}>
          <S.Title>
            {title}
            <S.Arrow $isOpen={isOpen} />
          </S.Title>
          {subTitle && (
            <S.SubTitle data-testid="accordion-subtitle">{subTitle}</S.SubTitle>
          )}
        </S.Header>

        {isOpen && contentHead}
        <S.Content
          ref={contentRef}
          $isOpen={isOpen}
          $maxHeight={maxHeight}
          data-testid="accordion-content"
        >
          <S.ContentBox $hasTopPadding={!contentHead}>{children}</S.ContentBox>
        </S.Content>
      </S.Wrapper>
    );
  }
);

Accordion.displayName = 'Accordion';
